import type { SessionService } from '@efdigital/ll-addon-auth';
import {
  CohortNavTemplate,
  ProgramNavTemplate,
  QuizOutput,
} from '@efdigital/ll-addon-content';
import { answerFor } from '@efdigital/ll-addon-content';
import QuizInput from '@efdigital/ll-addon-content/components/quiz/quiz-input';
import QuizQuestionComponent from '@efdigital/ll-addon-content/components/quiz/quiz-question';
import {
  type Answer,
  type AnswerValue,
  type QuizMark,
  type QuizQuestion,
  QuizQuestionType,
} from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { programTemplateParts } from '@efdigital/ll-admin-v2/components/parts.gts';
import ContentBreadcrumb from '@efdigital/ll-admin-v2/components/programs/content-breadcrumb.gts';
import { EditQuizCategories } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-categories.gts';
import { EditQuizCertificate } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-certificate.gts';
import { EditQuizInput } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-input.gts';
import { EditQuizOutput } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-output.gts';
import { EditQuizQuestionsInput } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/input-editor.gts';
import { EditQuizQuestionsOutput } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/output-editor.gts';
import { lookupQuestionManager } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/utils';
import { Sidebar } from '@efdigital/ll-admin-v2/components/sidebar/sidebar.gts';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type CohortContentQuizRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort/content/quiz';
import { Marker } from '@efdigital/ll-admin-v2/utils/mark-answer';
import { generateRandomAnswerValues } from '@efdigital/ll-admin-v2/utils/random-answer';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Type } from '@warp-drive/core/types/symbols';
import { pageTitle } from 'ember-page-title';
import { cell, resource, use } from 'ember-resources';

interface ContentQuizSignature {
  Args: {
    model: ModelFrom<CohortContentQuizRoute>;
    controller: unknown;
  };
}

const parts = programTemplateParts;
const marker = new Marker();

function eq(a: string, b: string): boolean {
  return a === b;
}

function quizAnswers(
  qids: QuizQuestion[],
  values: AnswerValue[],
  store: Store,
  params: Record<string, string>
) {
  const queryString: Record<string, string> = {};

  qids
    .filter((question) => !values.some((a) => a.qid == question.qid))
    .forEach((question) => {
      const answer: AnswerValue = {
        [Type]: DataTypeQuiz.AnswerValue,
        qid: question.qid,
        value: null,
      };
      if (question.type === QuizQuestionType.Hidden) {
        const query = question.hidden?.query;
        const defaultValue = question.hidden?.defaultValue || '';
        if (query) {
          if (query[0] === '$') {
            answer.value = params[query.substring(1)] || defaultValue;
          } else {
            answer.value = queryString[query] || defaultValue;
          }
        } else {
          answer.value = defaultValue;
        }
      }
      values.push(answer);
    });
}

type Focus = 'default' | 'question';
type View = 'input' | 'output';

class ContentQuiz extends Component<ContentQuizSignature> {
  @service declare session: SessionService;
  @service declare store: Store;

  @tracked focus: Focus = 'default';
  @tracked view: 'input' | 'output' = 'input';

  @tracked isolatedIndex = -1;

  cachedValues: AnswerValue[] = [];

  questionAdded = () => {
    const answers = this.args.model.answers;
    quizAnswers(this.args.model.quiz.questions, answers.values, this.store, {});
    return answers;
  };

  setFocus = (focus: Focus) => {
    this.focus = focus;
  };

  setView = (view: View) => {
    this.view = view;
  };

  @use answerValues = resource<AnswerValue[]>(() => {
    const knownValues = cell<AnswerValue[]>([]);

    const { values, hasChanges } = generateRandomAnswerValues(
      this.args.model.quiz.questions,
      this.cachedValues
    );
    if (hasChanges) {
      knownValues.set(values);
      this.cachedValues = values;
    }

    return () => knownValues.current;
  });

  get answer(): Answer {
    const values = [...this.answerValues];
    return {
      ...this.args.model.answers,
      values,
      mark: marker.mark(this.args.model.quiz, {
        ...this.args.model.answers,
        values,
      }) as QuizMark,
    };
  }

  isolate = (index: number) => {
    this.isolatedIndex = index;
  };

  get isIsolated(): boolean {
    return (
      this.isolatedIndex >= 0 &&
      this.isolatedIndex < this.args.model.quiz.questions.length
    );
  }

  get isolatedQuestion(): QuizQuestion {
    return this.args.model.quiz.questions[this.isolatedIndex]!;
  }

  <template>
    {{pageTitle "Quiz"}}

    <ContentBreadcrumb @model={{@model}} />

    <div class="mb-5">
      <EditContext as |context|>
        <Sidebar
          @disableScaling={{this.isIsolated}}
          class={{if this.isIsolated "bg-body-tertiary"}}
        >
          <:content>
            {{#if this.isIsolated}}
              {{#let
                (lookupQuestionManager this.isolatedQuestion)
                as |questionManager|
              }}
                {{! template-lint-disable no-inline-styles }}
                <div class="container py-3 sticky-top">
                  <div class="card rounded-4 shadow">
                    <div
                      class="card-header p-2 d-flex justify-content-between align-items-center text-bg-white rounded-top-4"
                    >
                      <h4 class="card-title h5 my-0 text-muted"><iconify-icon
                          inline
                          icon={{questionManager.icon}}
                          class="mx-2"
                        />
                        {{questionManager.label}}</h4>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary fw-bold"
                        {{on "click" (fn this.isolate -1)}}
                      >
                        <iconify-icon inline icon="ph:x-bold" />
                        Close Preview
                      </button>
                    </div>
                    <div class="card-body px-5 pt-5">
                      <QuizQuestionComponent
                        @readonly={{false}}
                        @question={{this.isolatedQuestion}}
                        @quiz={{@model.quiz}}
                        @answer={{answerFor
                          @model.answers
                          this.isolatedQuestion
                        }}
                      />
                    </div>
                  </div>
                </div>
              {{/let}}
            {{else}}
              <ProgramNavTemplate
                @program={{@model.program}}
                @cohort={{@model.cohort}}
                @parts={{parts}}
              />
              {{#if (eq this.focus "default")}}
                <CohortNavTemplate
                  @program={{@model.program}}
                  @cohort={{@model.cohort}}
                  @parts={{parts}}
                >
                  {{#let this.answer as |markedAnswer|}}
                    {{#if (eq this.view "input")}}
                      <QuizInput
                        @quiz={{@model.quiz}}
                        @answers={{markedAnswer}}
                      />
                    {{else}}
                      <QuizOutput
                        @quiz={{@model.quiz}}
                        @answers={{markedAnswer}}
                      />
                    {{/if}}
                  {{/let}}
                </CohortNavTemplate>
              {{/if}}
            {{/if}}
          </:content>
          <:sidebar>
            <div class="bg-white px-4 py-3 border">
              <nav class="nav nav-pills flex-column flex-sm-row">
                <button
                  type="button"
                  class="flex-sm-fill text-sm-center nav-link fw-bold
                    {{if (eq this.view 'input') 'active'}}"
                  disabled={{context.isEditing}}
                  {{on "click" (fn this.setView "input")}}
                >Input Form</button>
                <button
                  type="button"
                  class="flex-sm-fill text-sm-center nav-link fw-bold
                    {{if (eq this.view 'output') 'active'}}"
                  disabled={{context.isEditing}}
                  {{on "click" (fn this.setView "output")}}
                >Results Display</button>
              </nav>
            </div>
            <div class="px-3 py-4">
              {{#if (eq this.view "input")}}
                <EditQuizInput @item={{@model.quiz}} @context={{context}} />
              {{else if (eq this.view "output")}}
                <EditQuizOutput @item={{@model.quiz}} @context={{context}} />

                {{#if (eq @model.quiz.templateOutput "certificate")}}
                  <hr class="my-4" />
                  <EditQuizCertificate
                    @item={{@model.quiz}}
                    @context={{context}}
                  />
                {{/if}}
              {{/if}}

              <hr class="my-4" />

              {{#if (eq this.view "input")}}
                <EditQuizQuestionsInput
                  @item={{@model.quiz}}
                  @context={{context}}
                  @onQuestionAdded={{this.questionAdded}}
                  @isolate={{this.isolate}}
                />
              {{else if (eq this.view "output")}}
                <EditQuizCategories
                  @item={{@model.quiz}}
                  @context={{context}}
                />

                <hr class="my-4" />

                <EditQuizQuestionsOutput
                  @item={{@model.quiz}}
                  @context={{context}}
                  @onQuestionAdded={{this.questionAdded}}
                />
              {{/if}}
            </div>
          </:sidebar>
        </Sidebar>
      </EditContext>
    </div>
  </template>
}

export default ContentQuiz;
